'use client'

import { cn } from '@/lib/utils'
import {
  AccordionContent,
  AccordionItem,
  Accordion as AccordionShadcn,
  AccordionTrigger
} from '@/ui/Shadcn/accordion'
import { Button } from '@/ui/components'
import { createContext, useCallback, useContext, useState } from 'react'
import Richtext from '../Base/Richtext'

const AccordionCloseContext = createContext<
  ((itemValue: string) => void) | null
>(null)

export function Item({
  children,
  head,
  value,
  triggerClassName,
  contentClassName,
  closeButton,
  id,
  className,
  props
}: {
  children?: React.ReactNode
  head: React.ReactNode
  value: string
  triggerClassName?: string
  contentClassName?: string
  id?: string
  className?: string
  closeButton?: {
    text?: string
  }
  props?: React.ComponentProps<typeof AccordionItem>
}) {
  const closeItem = useContext(AccordionCloseContext)

  return (
    <AccordionItem
      className={cn(
        'group/accordion-item mb-xs border-none bg-neutral-200 text-neutral-600',
        className
      )}
      value={value}
      id={id}
      {...props}
    >
      <AccordionTrigger
        className={cn(
          'px-lg py-gutter cursor-pointer hover:no-underline [&_svg]:size-6 [&_svg]:text-neutral-600',
          triggerClassName,
          !children && 'pointer-events-none [&_svg]:hidden'
        )}
      >
        {head}
      </AccordionTrigger>
      <AccordionContent
        className={cn(
          'px-lg py-gutter textstyle-md-regular pb-padding-lg text-balance',
          contentClassName
        )}
      >
        {children}

        {closeButton && (
          <div className={'flex justify-center'}>
            <Button
              icon="material-symbols:expand-less"
              iconPosition="right"
              size="m"
              variant="ghost"
              onClick={() => closeItem?.(value)}
            >
              {closeButton?.text ?? 'Close'}
            </Button>
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  )
}

export interface AccordionItemProps {
  title?: string
  isOpen: boolean
  text?: string
}

export interface AccordionContainerProps {
  children: React.ReactNode
  items?: AccordionItemProps[]
}

export function Container({
  children,
  items,
  className,
  defaultOpen
}: {
  children: React.ReactNode
  items?: AccordionItemProps[]
  className?: string
  defaultOpen?: string
}) {
  const defaultValueValue = defaultOpen
    ? defaultOpen
    : (items?.findIndex((item) => item.isOpen).toString() ?? '')
  const [value, setValue] = useState(defaultValueValue)

  const closeItem = useCallback((itemValue: string) => {
    setValue((currentValue) => (currentValue === itemValue ? '' : currentValue))
  }, [])

  return (
    <AccordionCloseContext.Provider value={closeItem}>
      <AccordionShadcn
        type="single"
        collapsible
        className={cn('w-full', className)}
        value={value}
        onValueChange={setValue}
      >
        {children}
      </AccordionShadcn>
    </AccordionCloseContext.Provider>
  )
}

export function Accordion({
  items = [],
  id = Math.random().toString(36).substring(2, 15).toString()
}: {
  id?: string
  items?: AccordionItemProps[]
}) {
  const hasItems = items.length > 0
  const defaultValue = items.findIndex((item) => item.isOpen).toString() ?? ''
  const [value, setValue] = useState(hasItems ? `${id}-${defaultValue}` : '')

  const closeItem = useCallback((itemValue: string) => {
    setValue((currentValue) => (currentValue === itemValue ? '' : currentValue))
  }, [])

  if (!hasItems) {
    return null
  }

  return (
    <AccordionCloseContext.Provider value={closeItem}>
      <AccordionShadcn
        type="single"
        collapsible
        className="w-full"
        value={value}
        onValueChange={setValue}
      >
        {items.map((item, index) => (
          <Item
            key={index?.toString()}
            value={`${id}-${index.toString()}`}
            head={item.title}
          >
            <Richtext text={item.text ?? ''} />
          </Item>
        ))}
      </AccordionShadcn>
    </AccordionCloseContext.Provider>
  )
}
